import SEO from '../components/SEO';
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, Zap, TrendingUp, AlertCircle, CheckCircle2, RefreshCw, Clock, Target, Award, Users, BarChart3, Cloud, Check, Calendar, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const WhyUpgradeSAP = () => {
  const [formData, setFormData] = useState({ name: '', email: '', company: '', currentVersion: '', phone: '', consent: false });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/forms/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        message: `Current SAP Version: ${formData.currentVersion}`,
        source: 'Why Upgrade SAP Landing Page'
      });
      setSubmitted(true);
    } catch (error) {
      alert('Error submitting form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <SEO 
        title="Why Upgrade SAP Business One? | Stay Supported, Secure & Current"
        description="Keep SAP B1 supported and secure with regular upgrades. New features, security patches, compliance updates. Free upgrade testing with Ascarii."
        keywords="SAP B1 upgrade, SAP Business One version, ERP upgrade services, SAP support lifecycle, upgrade planning"
        canonical="https://www.ascarii.com/why-upgrade-sap"
      />

      {/* Hero */}
      <section className="relative min-h-[700px] bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        <div className="container mx-auto px-4 lg:px-8 py-20 relative z-10">
          <div className="max-w-5xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-block px-4 py-2 bg-blue-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full text-sm font-semibold mb-6">For Existing SAP Business One Customers</div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Keep SAP Business One<br />
                <span className="text-sky-300">Supported, Secure and Ready for What's Next</span>
              </h1>
              <div className="inline-block px-6 py-3 bg-cyan-400/20 backdrop-blur-sm border-2 border-cyan-400/50 rounded-xl mb-6">
                <p className="text-xl md:text-2xl text-cyan-100 font-semibold">
                  ✨ Free 30-Day Test Environment + No Upgrade Fees
                </p>
              </div>
              <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed">
                Upgrade to the latest SAP Business One with no professional services charges and a free 30-day testing environment - part of the Ascarii Experience Promise.
              </p>
              
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                {[
                  { icon: Shield, title: "Protected", desc: "Full support, security updates, compliance" },
                  { icon: Zap, title: "Productive", desc: "Modern interface, faster workflows" },
                  { icon: TrendingUp, title: "Future-Ready", desc: "Cloud, APIs, integrations" }
                ].map((benefit, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                    <benefit.icon className="w-6 h-6 text-cyan-300 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-bold text-white mb-1">{benefit.title}</h3>
                      <p className="text-blue-100 text-sm">{benefit.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0"><svg viewBox="0 0 1440 120" fill="none" className="w-full h-16"><path d="M0 0L60 10C120 20 240 40 360 46.7C480 53 600 47 720 43.3C840 40 960 40 1080 46.7C1200 53 1320 67 1380 73.3L1440 80V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V0Z" fill="white"/></svg></div>
      </section>

      {/* Risk Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">What Happens If You Stay Where You Are?</h2>
            <p className="text-xl text-gray-600">The hidden costs of running unsupported SAP versions</p>
          </div>
          
          <div className="space-y-6">
            {[
              { title: "End of Support", desc: "SAP stops providing fixes, updates, and compatibility patches for old versions. When something breaks, you're on your own." },
              { title: "Compliance & Regulatory Risk", desc: "UK VAT/MTD changes, new reporting requirements - old versions won't get the updates. Audit exposure increases." },
              { title: "Security Vulnerabilities", desc: "No security patches means growing exposure to cyber threats. Your business data becomes increasingly at risk." },
              { title: "Mounting Technical Debt", desc: "Manual workarounds pile up. The upgrade becomes more complex, expensive, and disruptive the longer you wait." },
              { title: "Competitive Disadvantage", desc: "Competitors on current versions gain automation, better insights, and integration capabilities you're missing." }
            ].map((risk, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, delay: i * 0.1 }} viewport={{ once: true }} className="flex items-start gap-4 p-6 bg-white rounded-xl border-l-4 border-red-600 shadow-md">
                <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">{risk.title}</h3>
                  <p className="text-gray-600 text-sm">{risk.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 p-8 bg-gradient-to-r from-blue-600 to-sky-600 rounded-2xl text-white text-center">
            <h3 className="text-2xl font-bold mb-4">There's a Safer Way Forward</h3>
            <p className="text-xl text-blue-100">Regular, planned upgrades keep you protected, productive, and future-ready.</p>
          </div>
        </div>
      </section>

      {/* Benefits Pillars */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">What You Gain When You Upgrade</h2>
            <p className="text-xl text-blue-100 max-w-4xl mx-auto">Three pillars of value from staying current</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { 
                icon: Shield, 
                title: "Protection & Compliance",
                strapline: "Stay supported and legal",
                desc: "Full SAP support with fixes and updates. Security patches for emerging threats. UK VAT/MTD and regulatory compliance built-in."
              },
              { 
                icon: Zap, 
                title: "Productivity & User Experience",
                strapline: "Work faster, make fewer mistakes",
                desc: "Modern Web Client with cleaner interface. Streamlined workflows for finance, inventory, and operations. Less clicking, fewer workarounds."
              },
              { 
                icon: Cloud, 
                title: "Future-Proofing & Integration",
                strapline: "Ready for what's next",
                desc: "Cloud-ready architecture for hybrid/hosted deployment. Stronger APIs for eCommerce, WMS, and automation. Easier to integrate modern tools."
              }
            ].map((pillar, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.1 }} viewport={{ once: true }} className="group p-8 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 hover:border-cyan-300 hover:bg-white/20 transition-all">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <pillar.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-2">{pillar.title}</h3>
                    <p className="text-sm text-cyan-300 font-medium mb-2">{pillar.strapline}</p>
                    <p className="text-blue-100 text-sm leading-relaxed">{pillar.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* The Ascarii Experience - Combined Section */}
      <section className="py-20 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto px-4 lg:px-8 max-w-6xl">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-2 bg-blue-600 text-white rounded-full text-sm font-bold mb-4">The Ascarii Experience</div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Risk-Free, No-Fee Upgrades to the Latest SAP Business One</h2>
          </div>

          {/* Promise Cards */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white rounded-2xl p-8 shadow-xl border-2 border-green-200">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center flex-shrink-0">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Free 30-Day Testing Environment</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Try the latest SAP Business One in a dedicated test environment for 30 days. Validate your key processes, train users, and test customisations - all before committing to go-live.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-xl border-2 border-blue-200">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500 to-sky-500 flex items-center justify-center flex-shrink-0">
                  <Award className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">No Upgrade Service Fees</h3>
                  <p className="text-gray-700 leading-relaxed">
                    We don't charge professional services fees for upgrading existing customers to the latest version. This is our standard commitment, not a promotion.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* You're Already Paying */}
          <div className="bg-gradient-to-r from-blue-600 to-sky-600 rounded-2xl p-8 md:p-10 text-white text-center shadow-2xl mb-12">
            <RefreshCw className="w-16 h-16 mx-auto mb-6 text-cyan-300" />
            <h3 className="text-3xl font-bold mb-4">You're Already Paying for This</h3>
            <p className="text-xl text-blue-100">
              Your SAP maintenance fees already include the right to upgrade to new versions. Not upgrading means paying for improvements you're not using.
            </p>
          </div>

          {/* How Upgrade Works */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">How the Upgrade Works</h3>
            <div className="flex flex-col md:flex-row items-stretch justify-center gap-4 md:gap-2">
              {[
                { step: "1", title: "Discovery & Review", desc: "We review your current setup, add-ons, customisations, and business priorities." },
                { step: "2", title: "Free 30-Day Test Environment", desc: "We set up your free testing environment on the latest SAP B1. Try it, test it, train on it." },
                { step: "3", title: "User Testing & Sign-Off", desc: "Your team validates key processes. We support you throughout the 30-day test period." },
                { step: "4", title: "Go-Live (No Upgrade Fees)", desc: "Execute upgrade with our support. No professional services charges - that's our promise." },
                { step: "5", title: "Optimisation", desc: "Post-upgrade support and training to help you maximise the new features." }
              ].map((step, i) => (
                <React.Fragment key={i}>
                  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.1 }} viewport={{ once: true }} className="bg-gradient-to-br from-slate-50 to-white rounded-xl p-6 border-2 border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all text-center w-full md:flex-1 md:max-w-[200px] flex flex-col">
                    <div className="w-12 h-12 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-xl mb-4 mx-auto">{step.step}</div>
                    <h4 className="font-bold text-gray-900 mb-2 text-sm">{step.title}</h4>
                    <p className="text-gray-600 text-xs leading-relaxed flex-1">{step.desc}</p>
                  </motion.div>
                  {i < 4 && (
                    <ArrowRight className="hidden md:block w-6 h-6 text-blue-600 flex-shrink-0 self-center" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Risk of Doing Nothing */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">The Real Cost of Waiting</h2>
            <p className="text-xl text-gray-600">Why regular small upgrades beat one big-bang later</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-8 bg-white rounded-2xl border-2 border-green-200 shadow-lg text-center">
              <h3 className="text-2xl font-bold text-green-700 mb-6">Regular Upgrades</h3>
              <ul className="space-y-3 inline-block text-left">
                {[
                  "Smaller functional changes",
                  "Simpler testing requirements",
                  "Less user shock and training",
                  "Predictable project timeline",
                  "Lower risk, lower cost"
                ].map((pro, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{pro}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="p-8 bg-white rounded-2xl border-2 border-red-200 shadow-lg text-center">
              <h3 className="text-2xl font-bold text-red-700 mb-6">Waiting Years</h3>
              <ul className="space-y-3 inline-block text-left">
                {[
                  "Massive functional gap to bridge",
                  "Complex testing across more changes",
                  "Major user retraining needed",
                  "Unpredictable timeline and costs",
                  "High risk, expensive, disruptive"
                ].map((con, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{con}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Ascarii */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Choose Ascarii for Your SAP Upgrade</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {[
              { icon: Award, title: "SAP Premier Partner", desc: "15+ years implementing and upgrading SAP B1 for UK businesses" },
              { icon: Target, title: "Proven Track Record", desc: "Smooth upgrades with minimal disruption. Fixed price options available." },
              { icon: Users, title: "No-Nonsense Approach", desc: "Clear communication, realistic timelines, transparent about what's involved." },
              { icon: Clock, title: "Typical 6-8 Weeks", desc: "Most SME upgrades completed in 6-8 weeks from planning to go-live." }
            ].map((diff, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.05 }} viewport={{ once: true }} className="bg-gradient-to-br from-slate-50 to-white rounded-xl p-6 border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all text-center">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/10 to-sky-500/10 flex items-center justify-center mb-4 mx-auto">
                  <diff.icon className="w-7 h-7 text-blue-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{diff.title}</h3>
                <p className="text-gray-600 text-sm">{diff.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Common Upgrade Questions</h2>
          </div>
          <div className="space-y-4">
            {[
              { q: "Will this disrupt my business?", a: "We upgrade outside business hours or at weekends to minimise impact. Most SMEs experience 4-8 hours of planned downtime. We test everything in a copy of your system first, so you know exactly what to expect." },
              { q: "How long does an SAP Business One upgrade take?", a: "Typical timeline is 6-8 weeks from initial planning to go-live. This includes discovery, test environment build, user acceptance testing, and the actual upgrade. We can accelerate for urgent situations." },
              { q: "Do we need to buy new licences?", a: "No. If you're on valid SAP maintenance, you already own the right to upgrade. The cost is the implementation project work, not new software licences." },
              { q: "What about our add-ons and customisations?", a: "We review all add-ons and customisations upfront. Most SAP-certified add-ons (like Boyum, Produmex, FocusPoint) have upgrade-friendly versions. Custom code may need updating - we test this in your test environment." },
              { q: "Can we move to the cloud at the same time?", a: "Yes. Many customers combine an upgrade with a move to AWS cloud hosting. This can simplify the project - upgrade and migrate in one go. We'll advise on the best approach for your situation." },
              { q: "How much testing do we have to do?", a: "We provide the test environment and run technical tests. You test your actual business processes (e.g., raising an order, posting an invoice, running key reports). Typically 1-2 weeks of user acceptance testing." },
              { q: "What if something breaks after go-live?", a: "We provide post-upgrade support as standard. Any issues are resolved quickly - we know your system inside out from the upgrade project. Most issues are minor configuration tweaks." },
              { q: "What's the cost?", a: "Ascarii doesn't charge professional services fees for upgrading existing SAP Business One customers to the latest version - this is part of our Ascarii Experience Promise. You also get a free 30-day testing environment. Your only cost is your existing SAP maintenance (which you're already paying and which entitles you to new versions). There are no hidden charges or surprise fees." },
              { q: "Can we test everything before we commit?", a: "Absolutely. We provide a free 30-day testing environment on the latest SAP Business One. You can validate all your key processes, train users, and test customisations before going live. This 'try before you commit' approach is part of the Ascarii Experience Promise." }
            ].map((faq, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.05 }} viewport={{ once: true }} className="bg-white rounded-xl border border-slate-200 hover:shadow-md transition-all">
                <button onClick={() => setExpandedFaq(expandedFaq === i ? null : i)} className="w-full p-6 text-left flex items-center justify-between focus:outline-none">
                  <h3 className="text-lg font-bold text-gray-900 pr-4">{faq.q}</h3>
                  {expandedFaq === i ? <ChevronUp className="w-5 h-5 text-blue-600 flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />}
                </button>
                {expandedFaq === i && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-700 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Assessment Form */}
      <section className="py-20 bg-gradient-to-br from-gray-100 via-blue-50 to-gray-100" id="assessment-form">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto items-start">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Book Your Free Upgrade Assessment</h2>
              <p className="text-xl text-gray-600 mb-8">We'll review your current setup and provide a clear upgrade roadmap</p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-md">
                  <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">No Obligation Assessment</h4>
                    <p className="text-sm text-gray-600">Free review of your system and upgrade options</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-md">
                  <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Target className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Fixed Price Options</h4>
                    <p className="text-sm text-gray-600">Transparent pricing with no hidden costs</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
              {submitted ? (
                <div className="bg-white rounded-2xl p-12 border-2 border-green-200 shadow-xl text-center">
                  <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-6">
                    <CheckCircle2 className="w-10 h-10 text-green-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">Thank You!</h2>
                  <p className="text-gray-600">We'll be in touch within one business day to discuss your SAP upgrade.</p>
                </div>
              ) : (
                <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-lg">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Get Started</h3>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Your Name *</label>
                      <input type="text" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="John Smith" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Work Email *</label>
                      <input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="john@company.com" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Company *</label>
                      <input type="text" required value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="Your Company Name" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Current SAP Version *</label>
                      <input type="text" required value={formData.currentVersion} onChange={(e) => setFormData({...formData, currentVersion: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="e.g., 9.3, 10.0" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                      <input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="+44 7700 900000" />
                    </div>
                    <div>
                      <label className="flex items-start gap-3">
                        <input type="checkbox" required checked={formData.consent} onChange={(e) => setFormData({...formData, consent: e.target.checked})} className="mt-1 w-5 h-5 text-blue-600 rounded" />
                        <span className="text-sm text-gray-700">I agree to Ascarii contacting me and have read the <a href="/privacy-policy" target="_blank" className="text-blue-600 hover:underline">Privacy Policy</a>. *</span>
                      </label>
                    </div>
                    <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 py-4 text-lg font-semibold">
                      {loading ? 'Sending...' : 'Book Upgrade Assessment'}
                    </Button>
                    <p className="text-xs text-gray-500 text-center">No obligations • Free assessment</p>
                  </form>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-blue-600 via-sky-700 to-cyan-600">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Ready to Unlock Your SAP Investment?</h2>
            <p className="text-xl md:text-2xl text-blue-100 mb-12 leading-relaxed">
              Stop paying for improvements you're not using. Upgrade now to stay supported, secure, and competitive.
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <a href="#assessment-form" className="inline-flex items-center justify-center rounded-xl bg-white text-blue-600 px-10 py-5 text-xl font-bold hover:bg-blue-50 shadow-2xl transition-all hover:scale-105">
                Book Upgrade Assessment
              </a>
              <a href="/contact" className="inline-flex items-center justify-center rounded-xl border-3 border-white text-white px-10 py-5 text-xl font-bold hover:bg-white/10 shadow-2xl transition-all hover:scale-105">
                Talk to an Expert
              </a>
            </div>
            <div className="mt-12 flex flex-wrap justify-center gap-8 text-white">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6 text-cyan-300" />
                <span className="text-lg">SAP Premier Partner</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6 text-cyan-300" />
                <span className="text-lg">15+ Years Experience</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6 text-cyan-300" />
                <span className="text-lg">Fixed Price Options</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky CTA Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-blue-600 text-white py-4 shadow-2xl z-50 hidden md:block">
        <div className="container mx-auto px-4 lg:px-8 flex items-center justify-between">
          <div>
            <p className="font-bold text-lg">Ready to upgrade SAP Business One?</p>
            <p className="text-blue-100 text-sm">Get your free assessment and upgrade roadmap</p>
          </div>
          <a href="#assessment-form">
            <Button className="bg-white text-blue-600 hover:bg-blue-50 px-6 py-3 font-semibold">
              Book Assessment
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
};

export default WhyUpgradeSAP;
